/** Tasks, frames and agents across projects for the hub, debounced like chat search. */
import { useEffect, useRef, useState, useSyncExternalStore } from "react";
import {
  searchWorkspace,
  type WorkspaceSearchKind,
  type WorkspaceSearchResults,
} from "@/api/workspace-search";

import { conversationBrowserAccountKey } from "@/hooks/conversation-browser-cache";
import { subscribeDesktopChatAuthScope } from "@/desktop/desktop-chat-auth-scope";
import { translate } from "@/i18n";
import { panelsSearchMessages } from "@/i18n/messages/panels-search";

export const HUB_SEARCH_DEBOUNCE_MS = 300;

export function useHubSearch({
  query,
  projectId,
  kinds,
}: {
  query: string;
  projectId: string | null;
  kinds: readonly WorkspaceSearchKind[];
}): {
  results: WorkspaceSearchResults;
  loading: boolean;
  error?: string | null;
} {
  const scope = useSyncExternalStore(
    subscribeDesktopChatAuthScope,
    conversationBrowserAccountKey,
    () => null,
  );
  const kindsKey = kinds.join(",");
  const [state, setState] = useState<{
    results: WorkspaceSearchResults;
    loading: boolean;
    error?: string | null;
  }>({
    results: {},
    loading: false,
  });
  const requestRef = useRef(0);

  useEffect(() => {
    const requestId = ++requestRef.current;
    let cancelled = false;
    if (!kindsKey) {
      setState({ results: {}, loading: false });
      return;
    }
    setState({ results: {}, loading: true });
    const timer = window.setTimeout(
      () => {
        searchWorkspace({
          query,
          projectId,
          kinds: kindsKey.split(",") as WorkspaceSearchKind[],
        })
          .then((results) => {
            if (
              !cancelled &&
              requestId === requestRef.current &&
              scope === conversationBrowserAccountKey()
            )
              setState({ results, loading: false });
          })
          .catch(() => {
            if (
              !cancelled &&
              requestId === requestRef.current &&
              scope === conversationBrowserAccountKey()
            )
              setState({
                results: {},
                loading: false,
                error: translate(panelsSearchMessages, "workspaceSearchFailed"),
              });
          });
      },
      query.trim() ? HUB_SEARCH_DEBOUNCE_MS : 0,
    );
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [kindsKey, projectId, query, scope]);

  return state;
}
